import {
  ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, OnInit, output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';

import { TranslateModule } from '@ngx-translate/core';
import { ControlType, SelectComponent, SimpleInputModule } from '@hglobal/common';
import { ShopOrder, FormType, PickupOrderForm, PickupLocation } from '@pt/models';

@Component({
  selector: 'pt-delivery-pickup',
  templateUrl: './pickup.component.html',
  styleUrls: ['./pickup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    SimpleInputModule,
    SelectComponent,
  ],
})
export class PickupComponent implements OnInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #formBuilder = inject(FormBuilder);

  order = input.required<ShopOrder>();
  form = input.required<FormType<PickupOrderForm, 'delivery'>>();
  pickupLocations = input<PickupLocation[]>([]);

  locationChanged = output<PickupLocation>();

  readonly controlTypes = ControlType;

  readonly #defLocationEffect = effect(() => {
    const order = this.order();
    const locations = this.pickupLocations();
    const prevLocationId = this.form().get('delivery').get('id').value;
    if (prevLocationId !== order.location.id) {
      const location = locations.find(loc => loc.id === order.location.id) ?? locations[0];
      this.#updateDelivery(location);
    }
  });

  ngOnInit(): void {
    this.form().removeControl('delivery');
    this.form().addControl('delivery', this.#formBuilder.group({
      location: null as string,
      address: null as string,
      id: null as number,
    }));

    this.form().get('delivery').get('id').valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(id => {
        const location = this.pickupLocations().find(loc => loc.id === id);
        this.#updateDelivery(location);
        this.locationChanged.emit(location);
      });
  }

  #updateDelivery(location?: PickupLocation): void {
    this.form().get('delivery').patchValue({
      location: location?.name || null,
      address: location?.address || null,
      id: location?.id || null,
    }, { emitEvent: false });
  }
}
